// Słownik znanych AVL ID Teltonika → czytelna nazwa + przelicznik
// Źródło: wiki.teltonika-gps.com + obserwacja FMC150 + analiza Traccar 6.13.3

export interface CanFieldDef {
  name: string;
  unit: string;
  divisor?: number;       // jeśli trzeba podzielić wartość
  precision?: number;     // ile miejsc po przecinku
  max?: number;           // wartości > max traktowane jako nieprawidłowe (lądują w "nieznane pola")
  format?: 'hex';         // sposób wyświetlania surowej wartości (np. bitmaska)
  ignitionRequired?: boolean; // ukryj gdy ignition=false (dane silnika bez sensu na zimno)
}

const CAN_FIELDS: Record<string, CanFieldDef> = {
  // CAN / FMS
  io13:  { name: 'Spalanie GPS',       unit: 'l/100km', divisor: 100, precision: 1, ignitionRequired: true },
  io15:  { name: 'Eco Score',          unit: '',    divisor: 100,     precision: 0 },
  // io81 (FMC150): prędkość OBD z wbudowanego CAN, 1:1 km/h, zweryfikowana
  // korelacją z GPS 09.07.2026.
  io81:  { name: 'Prędkość OBD (CAN)', unit: 'km/h' },
  io82:  { name: 'Przepustnica',       unit: '%',                                    ignitionRequired: true },
  io84:  { name: 'Paliwo (objętość)', unit: 'l',   divisor: 10,      precision: 1 },
  io85:  { name: 'RPM silnika (CAN)', unit: 'rpm',                                   ignitionRequired: true },
  io87:  { name: 'Przebieg CAN',      unit: 'km',  divisor: 1000,    precision: 0 },
  io89:  { name: 'Poziom paliwa',     unit: '%' },
  io103: { name: 'Czas pracy silnika', unit: 'min' },
  io105: { name: 'Przebieg GPS',       unit: 'km',  divisor: 1000,    precision: 0 },
  // io107 (FMC150): licznik narastający zużytego paliwa. FMC150 ma WBUDOWANY
  // stack LV-CAN (technologia SEO Electronics, następca FMB140 — bez zewnętrznej
  // przystawki); dla źródła LV-CAN io107 = liters×10 → ÷10. Zweryfikowane
  // empirycznie 09.07.2026: Δ32 raw / 56.65 km = 3.2 L = 5.65 l/100km
  // (Toyota, deviceid=2). Wiki FMB podaje "ml" (÷1000) — dotyczy innego źródła
  // tego ID; u nas daje wartości 100× za małe. NIE "poprawiać" wg wiki bez
  // ponownej weryfikacji na danych z jazdy.
  io107: { name: 'Zużyte łącznie (CAN)', unit: 'L', divisor: 10, precision: 1 },
  io113: { name: 'Bateria trackera',  unit: '%' },
  io115: { name: 'Temp. silnika',     unit: '°C',  divisor: 10,      precision: 1,  ignitionRequired: true },
  io123: { name: 'Ciśnienie oleju',   unit: 'kPa', max: 1000,                        ignitionRequired: true },
  io200: { name: 'Tachograf (status)', unit: '' },
  io206: { name: 'Przebieg (counted)', unit: 'km', divisor: 1000,    precision: 1 },
  io237: { name: 'Jazda (aktywność)', unit: '' },
  io238: { name: 'Kierowca (ID)',     unit: '' },

  // Horta Signal Mini — CP1 (Rekord PL §4.7)
  rpm:             { name: 'RPM silnika',       unit: 'rpm',   max: 8000, ignitionRequired: true },
  throttle:        { name: 'Przepustnica',      unit: '%',     precision: 1, max: 110, ignitionRequired: true },
  coolantTemp:     { name: 'Temp. płynu',       unit: '°C',    max: 210 },
  canOdometer:     { name: 'Przebieg CAN',      unit: 'km',    precision: 1 },
  fuelLevel:       { name: 'Poziom paliwa',     unit: '%',     precision: 1, max: 100 },
  adBlue:          { name: 'AdBlue',            unit: '%',     precision: 1, max: 100 },
  adBlueRange:     { name: 'Zasięg AdBlue',     unit: 'km' },
  totalFuelUsed:   { name: 'Paliwo zużyte CAN', unit: 'L',     precision: 1 },
  obdSpeed:        { name: 'Prędkość OBD',      unit: 'km/h',  max: 308, ignitionRequired: true },
  estimatedRange:  { name: 'Zasięg est.',       unit: 'km' },
  checkEngine:     { name: 'Check Engine',      unit: '' },
  // Standardowe
  engineMinutes:     { name: 'Czas pracy silnika',   unit: 'min' },
  engineIdleMinutes: { name: 'Czas na biegu jałowym', unit: 'min' },
  power:         { name: 'Napięcie',       unit: 'V',  precision: 2 },
  battery:       { name: 'Bateria',        unit: 'V',  precision: 2 },
  sat:           { name: 'Satelity',       unit: '' },
  rssi:          { name: 'Sygnał GSM',     unit: '/5' },
  odometer:      { name: 'Dystans trasy',  unit: 'm' },
  hours:         { name: 'Mth silnika',    unit: 'h',  divisor: 3600000, precision: 1 },
};

export default CAN_FIELDS;

// Formatuje surową wartość io* wg divisor/precision ze słownika — jedyne
// miejsce, które powinno dzielić wartości CAN; nie duplikować przeliczników
// lokalnie w komponentach (patrz io107: sprzeczność ÷1000 vs ÷10 w historii).
export const formatCanValue = (key: string, raw: number): string => {
  const def = CAN_FIELDS[key];
  const divisor = def?.divisor ?? 1;
  const precision = def?.precision ?? 0;
  return (raw / divisor).toFixed(precision);
};

// Pola które NIE są danymi CAN/pojazdu — pomijamy w sekcji "dane pojazdu"
export const SKIP_FIELDS = new Set([
  'priority', 'event', 'ignition', 'motion', 'distance',
  'totalDistance', 'tripOdometer', 'operator', 'pdop', 'hdop',
  'axisX', 'axisY', 'axisZ', 'ip', 'in1', 'in2', 'in3', 'in4',
  'out1', 'out2', 'io24', 'io68', 'io69',
  'io6',   // Analog Input 2 [mV] — stałe napięcie, nie RPM
  'io9',   // Analog Input 1 [mV] — bez znaczenia dla użytkownika
  'io90',  // Door Status bitmask — niejednoznaczna interpretacja
  'io11',  // ICCID lub inny duży licznik — niejasna jednostka
  'io132', // Status drzwi jako 64-bit hex — zbyt duże wartości
  // Horta internal — shown via HortaCanCard, not as raw CAN rows
  'coordSource', 'canLastUpdate', 'fixType',
  // Horta device-level attrs (in tc_devices, not position — but guard anyway)
  'isHybrid', 'tankCapacity1', 'tankCapacity2', 'tankCapacity3', 'tankCapacity4',
  // Raw FLA block fuel (×0.1, not meaningful in CAN section — use fuelLevel% instead)
  'fuel',
  // Engine on/off (ignition je czytelniejszy; engine = osobna flaga silnika)
  'engine',
]);
